// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { type ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { processError } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';

export type FetchPolicy =
	| 'cache-first'
	| 'cache-and-network'
	| 'network-only'
	| 'no-cache'
	| 'store-or-network';

export type ErrorPolicy = 'none' | 'all';

export type ApolloCompatibleQueryArgs<TData> = {
	fetchData: () => Promise<TData>;
	fetchPolicy?: FetchPolicy;
	errorPolicy?: ErrorPolicy;
	skip?: boolean;
	onCompleted?: (data: TData) => void;
	onError?: (error: ApolloErrorInterop) => void;
	// Optional cache read and write functions for external cache systems (e.g., sweet-state)
	getCachedData?: () => TData | undefined;
	setCachedData?: (data: TData) => void | Promise<void>;
	convertToApolloError: (error: Error) => ApolloErrorInterop | Promise<ApolloErrorInterop>;
};

export type ApolloCompatibleQueryResult<TData> = {
	data: TData;
	error?: ApolloErrorInterop;
};

export async function apolloCompatibleQuery<TData>({
	fetchData,
	fetchPolicy,
	errorPolicy = 'none',
	skip = false,
	onCompleted,
	onError,
	getCachedData,
	setCachedData,
	convertToApolloError,
}: ApolloCompatibleQueryArgs<TData>): Promise<ApolloCompatibleQueryResult<TData>> {
	if (skip) {
		return { data: undefined as TData, error: undefined };
	}

	// cache-first / store-or-network: return cached data if it exists or fetch from network
	if ((fetchPolicy === 'cache-first' || fetchPolicy === 'store-or-network') && getCachedData) {
		const cachedData = getCachedData();
		if (cachedData !== undefined) {
			onCompleted?.(cachedData);
			return { data: cachedData, error: undefined };
		}
	}

	// cache-and-network: return cached data immediately, fetch in background
	if (fetchPolicy === 'cache-and-network' && getCachedData) {
		const cachedData = getCachedData();
		if (cachedData !== undefined) {
			fetchData().catch(() => {});
			onCompleted?.(cachedData);
			return { data: cachedData, error: undefined };
		}
	}

	// network-only or cache miss: fetch from network
	try {
		const data = await fetchData();

		if (fetchPolicy !== 'no-cache' && setCachedData) {
			await setCachedData(data);
		}

		onCompleted?.(data);
		return { data, error: undefined };
	} catch (error) {
		const apolloError = await Promise.resolve(convertToApolloError(error));

		if (apolloError.graphQLErrors?.length) {
			apolloError.graphQLErrors.forEach((e) => {
				processError(e);
			});
		}
		onError?.(apolloError);

		if (errorPolicy === 'all') {
			return {
				data: undefined as TData,
				error: apolloError,
			};
		}

		// errorPolicy === 'none': throw the error
		throw apolloError;
	}
}
